import React, { useEffect, useState } from 'react';
import { X, MessageSquare, Mic } from 'lucide-react';
import { toast } from 'react-toastify';

const PassingScoreModal = ({
  isOpen, onClose, onSubmit, defaultScore, jobId, onUpdate, jobDetails, screenType,
  updateExistingStudents, setUpdateExistingStudents
}) => {
  const [passingScore, setPassingScore] = useState(defaultScore);
  const [modality, setModality] = useState('inherit'); // "audio" | "text" | "inherit"

  useEffect(() => {
    const cfg = jobDetails?.screeningConfig || {};
    const round = cfg?.[screenType] || {};
    setPassingScore(round.passingScore ?? 60);
    setModality(round.modality || 'inherit');
  }, [jobDetails, screenType]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      // onSubmit signature is (passingScore, updateExisting, modality).
      // Existing call sites pass only the first two — extra arg is safely ignored.
      await onSubmit(passingScore, updateExistingStudents, modality);
      onClose();
    } catch (error) {
      console.error('Error setting passing score:', error);
      toast.error('Failed to set passing score');
    }
  };

  const ModalityChip = ({ value, icon: Icon, label, sub }) => {
    const active = modality === value;
    return (
      <button
        type="button"
        onClick={() => setModality(value)}
        className={`flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition ${
          active ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'
        }`}
      >
        <Icon className="w-5 h-5" />
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs opacity-70">{sub}</div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[26rem] max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Configure round</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Modality picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview format
            </label>
            <div className="flex gap-2">
              <ModalityChip value="audio"   icon={Mic}            label="Audio"    sub="ElevenLabs voice + STT" />
              <ModalityChip value="text"    icon={MessageSquare}  label="Text"     sub="Classic chat" />
              <ModalityChip value="inherit" icon={MessageSquare}  label="Default"  sub="Use job default" />
            </div>
          </div>

          {/* Passing score */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum score required for shortlisting (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={passingScore}
              onChange={(e) => setPassingScore(Number(e.target.value))}
              className="w-full p-2 border rounded focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={updateExistingStudents}
                onChange={(e) => setUpdateExistingStudents(e.target.checked)}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              Update status of existing students based on new passing score
            </label>
            {updateExistingStudents && (
              <p className="mt-1 text-sm text-gray-500 italic">
                This will update the status (shortlisted/rejected) of all students who have already completed the test based on the new passing score.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassingScoreModal;
